import type { TrackingIcon, TrackingIconSlot } from '../models/TrackingIcon';

export interface TrackingIconScheduleSegment {
  from: Date;
  to: Date;
  icon: TrackingIcon | null;
}

interface SpecialPeriod {
  from: number;
  to: number;
  icon: TrackingIcon;
}

const assertValidDate = (date: Date) => {
  if (!(date instanceof Date) || !Number.isFinite(date.getTime())) {
    throw new Error('La fecha de consulta no es válida.');
  }
};

const readBoundary = (value: string | null, fallback: number) => {
  if (value === null) return fallback;
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) {
    throw new Error('Un ícono tiene una fecha de vigencia no válida.');
  }
  return timestamp;
};

/** Covers [from, to), using an enabled special before the slot's default. */
export const buildTrackingIconSchedule = (
  icons: TrackingIcon[],
  slot: TrackingIconSlot,
  from: Date,
  to: Date
): TrackingIconScheduleSegment[] => {
  assertValidDate(from);
  assertValidDate(to);
  const start = from.getTime();
  const end = to.getTime();
  if (start >= end) {
    throw new Error('El inicio de la consulta debe ser anterior al fin.');
  }

  let defaultIcon: TrackingIcon | null = null;
  const specialPeriods: SpecialPeriod[] = [];
  for (const icon of icons) {
    if (!icon.enabled || icon.slot !== slot) continue;
    if (icon.isDefault) {
      if (icon.dateFrom !== null || icon.dateTo !== null) {
        throw new Error(
          'Un ícono predeterminado no puede tener fechas de vigencia.'
        );
      }
      if (defaultIcon) {
        throw new Error(
          'Hay más de un ícono predeterminado habilitado para este tipo.'
        );
      }
      defaultIcon = icon;
      continue;
    }
    const periodFrom = readBoundary(icon.dateFrom, -Infinity);
    const periodTo = readBoundary(icon.dateTo, Infinity);
    if (periodFrom >= periodTo) {
      throw new Error('Un ícono tiene un período de vigencia no válido.');
    }
    specialPeriods.push({ from: periodFrom, to: periodTo, icon });
  }

  specialPeriods.sort((left, right) => left.from - right.from);
  for (let index = 1; index < specialPeriods.length; index += 1) {
    if (specialPeriods[index].from < specialPeriods[index - 1].to) {
      throw new Error(
        'Hay íconos especiales habilitados con períodos superpuestos.'
      );
    }
  }

  const segments: TrackingIconScheduleSegment[] = [];
  const append = (
    segmentFrom: number,
    segmentTo: number,
    icon: TrackingIcon | null
  ) => {
    if (segmentFrom >= segmentTo) return;
    const previous = segments[segments.length - 1];
    if (
      previous &&
      previous.to.getTime() === segmentFrom &&
      (previous.icon === icon ||
        (previous.icon && icon && previous.icon.id === icon.id))
    ) {
      previous.to = new Date(segmentTo);
      return;
    }
    segments.push({
      from: new Date(segmentFrom),
      to: new Date(segmentTo),
      icon,
    });
  };

  let cursor = start;
  for (const period of specialPeriods) {
    if (period.to <= start) continue;
    if (period.from >= end) break;
    const periodFrom = Math.max(start, period.from);
    const periodTo = Math.min(end, period.to);
    append(cursor, periodFrom, defaultIcon);
    append(periodFrom, periodTo, period.icon);
    cursor = periodTo;
  }
  append(cursor, end, defaultIcon);
  return segments;
};

/** Local calendar month, with its final midnight excluded. */
export const monthBounds = (date: Date): { from: Date; to: Date } => {
  assertValidDate(date);
  const from = new Date(date);
  from.setDate(1);
  from.setHours(0, 0, 0, 0);
  const to = new Date(from);
  to.setMonth(to.getMonth() + 1);
  to.setHours(0, 0, 0, 0);
  return { from, to };
};

/** Monday-first grid, always five or six weeks; dates follow local DST rules. */
export const calendarDays = (date: Date): Date[] => {
  const { from, to } = monthBounds(date);
  const offset = (from.getDay() + 6) % 7;
  const lastDay = new Date(to);
  lastDay.setDate(0);
  const count = Math.max(35, Math.ceil((offset + lastDay.getDate()) / 7) * 7);
  return Array.from({ length: count }, (_, index) => {
    const day = new Date(from);
    day.setDate(1 - offset + index);
    day.setHours(0, 0, 0, 0);
    return day;
  });
};
